import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plug, RefreshCw, Send, CheckCircle2, XCircle } from "lucide-react";

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const money = (v) => (v === null || v === undefined ? "—" : `A$${Number(v).toFixed(2)}`);
const num = (v) => (v === null || v === undefined ? "0" : String(v));

export default function NavScoraIntegration() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const period = currentPeriod();

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    if (user && user.is_owner === false) navigate("/more", { replace: true });
  }, [user, navigate]);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data } = await api.get("/admin/navscora/status");
      setStatus(data);
    } catch (e) {
      toast.error(apiError(e));
    }
    setLoadingStatus(false);
  };

  useEffect(() => { loadStatus(); }, []);

  const runPreview = async () => {
    setPreviewing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/admin/navscora/preview", { period });
      setSnapshot(data.snapshot);
      toast.success("Preview generated (nothing sent)");
    } catch (e) {
      toast.error(apiError(e));
    }
    setPreviewing(false);
  };

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/navscora/sync", { period });
      setSyncResult(data);
      if (data?.result?.ok) toast.success("Snapshot sent to NavScora");
      else toast.error("NavScora sync failed");
    } catch (e) {
      setSyncResult({ result: { ok: false, error: apiError(e) } });
      toast.error(apiError(e));
    }
    setSyncing(false);
    setConfirmOpen(false);
  };

  const fin = snapshot?.financials || {};
  const met = snapshot?.metrics || {};
  const subs = snapshot?.subscriptions || {};

  const rows = [
    ["Subscriptions (active / trial / new / cancelled / past due)",
      `${num(subs.active)} / ${num(subs.trial)} / ${num(subs.new)} / ${num(subs.cancelled)} / ${num(subs.past_due)}`],
    ["Gross sales", money(fin.gross_sales)],
    ["Discounts", money(fin.discounts)],
    ["Refunds", money(fin.refunds)],
    ["Net sales", money(fin.net_sales)],
    ["Tax collected (GST)", money(fin.tax_collected)],
    ["Stripe fees", money(fin.stripe_fees)],
    ["Stripe adjustments", money(fin.stripe_adjustments)],
    ["Net proceeds", money(fin.net_proceeds)],
    ["MRR", money(met.mrr)],
    ["ARR", money(met.arr)],
    ["ARPU", money(met.arpu)],
  ];

  return (
    <AppShell>
      <PageHeader title="NavScora Integration" subtitle="Owner only · commercial snapshot sync" onBack={() => navigate("/more")} />
      <div className="px-5 space-y-3 df-fade-in" data-testid="navscora-page">

        {/* Connection status */}
        <div className="df-card p-4" data-testid="navscora-status-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center"><Plug size={18} /></span>
            <div className="flex-1">
              <div className="font-medium text-slate-900">Connection</div>
              <div className="text-xs text-slate-500" data-testid="navscora-env">
                {loadingStatus ? "Checking…" : `Environment: ${status?.environment ?? "—"}`}
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-xl df-pressable" onClick={loadStatus} disabled={loadingStatus} data-testid="navscora-refresh-status">
              <RefreshCw size={14} className={loadingStatus ? "animate-spin" : ""} />
            </Button>
          </div>
          {!loadingStatus && status && (
            <div className="text-xs text-slate-600 space-y-1" data-testid="navscora-status-detail">
              <div>Configured: <b>{status.configured ? "Yes" : "No"}</b> · State: <b>{status.state}</b></div>
              <div>Base URL set: {status.has_base_url ? "Yes" : "No"} · ID set: {status.has_integration_id ? "Yes" : "No"} · Key set: {status.has_integration_key ? "Yes" : "No"}</div>
              {status.status?.last_success && <div>Last success: {status.status.last_success} ({status.status.last_period})</div>}
              {status.status?.last_error && <div className="text-red-600">Last error: {status.status.last_error} {status.status.last_error_at ? `(${status.status.last_error_at})` : ""}</div>}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="df-card p-4 space-y-3" data-testid="navscora-actions-card">
          <div className="text-sm font-semibold text-slate-900">Current month · {period}</div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Preview builds the commercial snapshot from Stripe and sends nothing. Sync sends this month's snapshot to NavScora Admin.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 h-11 rounded-xl df-pressable" onClick={runPreview} disabled={previewing} data-testid="navscora-preview-button">
              <RefreshCw size={16} className={`mr-1.5 ${previewing ? "animate-spin" : ""}`} /> {previewing ? "Previewing…" : "Preview current month"}
            </Button>
            <Button className="flex-1 h-11 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable" onClick={() => setConfirmOpen(true)} disabled={syncing || !status?.configured} data-testid="navscora-sync-button">
              <Send size={16} className="mr-1.5" /> Sync current month
            </Button>
          </div>
          {!status?.configured && !loadingStatus && (
            <p className="text-[11px] text-amber-600">Sync is disabled until the integration is configured in this environment.</p>
          )}
        </div>

        {/* Snapshot preview */}
        {snapshot && (
          <div className="df-card p-4" data-testid="navscora-snapshot-card">
            <div className="text-sm font-semibold text-slate-900 mb-2">Snapshot · {snapshot.period} · {snapshot.currency}</div>
            <div className="divide-y divide-slate-100">
              {rows.map(([label, val]) => (
                <div key={label} className="flex items-start justify-between py-1.5 gap-3">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className="text-xs font-medium text-slate-900 text-right">{val}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Dry run — no data was sent to NavScora.</p>
          </div>
        )}

        {/* Sync result */}
        {syncResult && (
          <div className="df-card p-4" data-testid="navscora-sync-result">
            <div className={`flex items-center gap-2 text-sm font-semibold ${syncResult.result?.ok ? "text-[#0D786A]" : "text-red-600"}`}>
              {syncResult.result?.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
              {syncResult.result?.ok ? "Sync succeeded" : "Sync failed"}
            </div>
            <div className="text-xs text-slate-600 mt-1 space-y-0.5">
              {syncResult.environment && <div>Environment: {syncResult.environment}</div>}
              {syncResult.result?.status !== undefined && <div>HTTP status: {String(syncResult.result.status)}</div>}
              {syncResult.result?.attempts !== undefined && <div>Attempts: {syncResult.result.attempts}</div>}
              {syncResult.result?.error && <div className="text-red-600">Error: {syncResult.result.error}</div>}
              {syncResult.idempotency_key && <div className="break-all">Idempotency key: {syncResult.idempotency_key}</div>}
            </div>
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !syncing && setConfirmOpen(o)}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Send snapshot to NavScora?</DialogTitle>
            <DialogDescription>
              Send this month's Molaroo commercial snapshot ({period}) to NavScora Admin? Only Stripe-derived commercial totals are sent — no customer or bookkeeping data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={syncing} className="rounded-xl" data-testid="navscora-sync-cancel">Cancel</Button>
            <Button onClick={runSync} disabled={syncing} className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]" data-testid="navscora-sync-confirm">
              {syncing ? "Sending…" : "Send snapshot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
